import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ANALYTICS_AVAILABILITY, type AnalyticsAvailability, type AnalyticsPeriod } from '@/lib/social-analytics/analyticsFilters';
import { isMediaType, MEDIA_TYPES, mediaTypeKey, type MediaType } from '@/lib/social-analytics/mediaTypeKey';
import { MultiSelectPopover } from './MultiSelectPopover';

type ContentTableToolbarProps = {
  search: string;
  availability: AnalyticsAvailability | null;
  mediaTypes: readonly MediaType[];
  period: AnalyticsPeriod;
  /** The period switch is meaningless on "All time", where both options select every post. */
  showPeriod: boolean;
  onSearchChange: (value: string) => void;
  onAvailabilityChange: (value: AnalyticsAvailability | null) => void;
  onMediaTypesChange: (value: MediaType[]) => void;
  onPeriodChange: (value: AnalyticsPeriod) => void;
};

/** Table-only filters: they change the content table and nothing else on the page. */
export const ContentTableToolbar = ({
  search,
  availability,
  mediaTypes,
  period,
  showPeriod,
  onSearchChange,
  onAvailabilityChange,
  onMediaTypesChange,
  onPeriodChange,
}: ContentTableToolbarProps) => {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t('analytics.content.filtersLabel')} className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
      <div className="relative min-w-0 flex-1 sm:max-w-sm">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          className="h-9 bg-background pl-8"
          placeholder={t('analytics.content.searchPlaceholder')}
          aria-label={t('analytics.content.searchPlaceholder')}
        />
      </div>
      <MultiSelectPopover
        label={t('analytics.filters.mediaTypes')}
        allLabel={t('analytics.filters.allMediaTypes')}
        countLabel={t('analytics.filters.mediaTypesCount', { count: mediaTypes.length })}
        options={MEDIA_TYPES.map((type) => ({ value: type, label: t(mediaTypeKey(type)) }))}
        value={mediaTypes}
        onChange={(next) => onMediaTypesChange(next.filter(isMediaType))}
        idPrefix="analytics-media-type"
      />
      <Select value={availability ?? 'all'} onValueChange={(value) => onAvailabilityChange(value === 'all' ? null : value as AnalyticsAvailability)}>
        <SelectTrigger aria-label={t('analytics.filters.availability')} className="h-9 w-[160px] bg-background">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('analytics.filters.availabilityValues.all')}</SelectItem>
          {ANALYTICS_AVAILABILITY.map((value) => (
            <SelectItem key={value} value={value}>{t(`analytics.filters.availabilityValues.${value}`)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {showPeriod && (
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={period}
          onValueChange={(value) => { if (value === 'active' || value === 'published') onPeriodChange(value); }}
          aria-label={t('analytics.filters.period')}
        >
          <ToggleGroupItem value="active" className="h-9 px-3 text-xs">{t('analytics.filters.periodActive')}</ToggleGroupItem>
          <ToggleGroupItem value="published" className="h-9 px-3 text-xs">{t('analytics.filters.periodPublished')}</ToggleGroupItem>
        </ToggleGroup>
      )}
    </div>
  );
};
